import React, { type ReactNode } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";

import { useMeasuredElementHeight } from "@src/providers/layout/useMeasuredElementHeight";

import type { WorkspaceCapabilities } from "./apiPort";
import { TitleBar } from "./components/01-TitleBar/TitleBar";
import { GateProgressStrip } from "./components/02-GateRail/GateProgressStrip";
import { summarizeGates } from "./components/02-GateRail/gateStatus";
import { EditorPanel } from "./components/03-EditorPanel/EditorPanel";
import { BottomSection } from "./components/05-BottomSection/BottomSection";
import { ControlsPanel } from "./components/ControlsPanel/ControlsPanel";
import { PublishReviewModal } from "./components/modals/PublishReviewModal/PublishReviewModal";
import { RollbackConfirmModal } from "./components/modals/RollbackConfirmModal/RollbackConfirmModal";
import { SecretsManagementModal } from "./components/modals/SecretsManagementModal/SecretsManagementModal";
import { VersionHistoryPanel } from "./components/VersionHistory/VersionHistoryPanel";
import {
  type WorkspaceBodyView,
  WorkspaceViewSwitch,
} from "./components/VersionHistory/WorkspaceViewSwitch";
import type { WorkspaceDomainConfig } from "./domainConfig";
import { isWorkspaceSaveDisabled } from "./saveAvailability";
import type { WorkspaceViewModel } from "./types";

/**
 * The editor row never gives up its last {@link EDITOR_ROW_MIN_HEIGHT}px to the
 * resizable bottom panel. The split box is measured rather than assumed so the
 * panel's drag ceiling is real available space — a px ceiling taken from a
 * constant lets the column grow past its container, which is what produced the
 * doubled scrollbars.
 */
const EDITOR_ROW_MIN_HEIGHT = 96;

export interface CodegenWorkspaceProps<
  GateKey extends string,
  TabKey extends string,
> {
  /** Everything domain-specific, as data. */
  config: WorkspaceDomainConfig<GateKey, TabKey>;
  vm: WorkspaceViewModel<GateKey, TabKey>;
  /** Derived once from the port so render code never optional-chains. */
  capabilities: WorkspaceCapabilities;
  /**
   * Domain alerts rendered above the title bar (unmet preconditions, unsaved
   * parent records, …). The shell has no opinion about what they say.
   */
  banners?: ReactNode;
  /** Optional domain metadata rendered below the title bar. */
  headerDetails?: ReactNode;
  /** Optional staff-only recorded-executions view. */
  recentRuns?: ReactNode;
  /** Whether the code view includes the resizable bottom tabs. */
  showBottomSection?: boolean;
  /**
   * How much chrome the shell draws.
   *
   * - `"full"` (default) — the shell owns the title bar. This is the standalone
   *   workspace every caller had before embedding existed.
   * - `"embedded"` — the title bar is suppressed, because the caller has its own
   *   header carrying the entity name and the write actions.
   *
   * The suppression stops at the title bar. The gate rail is *content* — it
   * reports the state of the code below it and, when the bottom section is
   * present, opens gate details there. Everything except the title bar renders
   * identically in both modes.
   *
   * Suppression is a caller-side layout choice, so it is a prop rather than
   * domain config: the same domain can be shown standalone in one place and
   * embedded in another.
   */
  chrome?: "full" | "embedded";
  /** The entity is live / the user lacks edit rights. */
  readOnly: boolean;
  /**
   * `true` while a domain-owned AI author is rewriting the source out-of-band
   * (Agentic Logic's Atlas chat session, its repair loop). The editor holds
   * as "<author> is updating…" for the duration. The shell's own AI panel
   * reports its pending state internally and needs no flag here.
   */
  isAiUpdating?: boolean;
  /** Additionally disables actions that need a persisted parent record. */
  disablePersistedActions: boolean;
  /** Hover copy explaining a non-obvious disabled state. */
  disabledActionHint?: string;
  /** Rail pill click — the binding owns the focused-gate state its tabs read. */
  onSelectGate: (gateKey: GateKey) => void;
  /** Opens the domain's AI authoring surface with the failed check. */
  onAskAiFix?: () => void;
  onClose: () => void;
  /**
   * Renders a version's read-only code inside its expanded version-history
   * card. Supplied by the domain because the version-detail read is a
   * per-domain query. Omitted → cards have no expand affordance.
   */
  renderVersionCode?: (version: number) => ReactNode;
  /**
   * Whether this workspace hosts the entity's version history at all.
   *
   * `true` (the default, and every host's behaviour before this flag) — the
   * shell draws the "Version history" segment and its panel, which is the only
   * place the entity's versions live.
   *
   * `false` — the page AROUND the workspace owns version history, and the
   * segment is withheld along with the panel. Agentic Logic promoted versions
   * to a top-level tab of the rule page; leaving a second copy in here would
   * mean two lists with two rollback buttons, drifting apart on their own.
   */
  showVersionHistory?: boolean;
  /** Domain-specific ceiling on retained versions, or omitted for no limit. */
  versionLimit?: { limit: number; warnAt: number } | null;
  /** Copy for the rollback confirm modal's impact warning. */
  rollbackImpactCopy: (version: number) => string;
  /**
   * Set when the entity has reached a terminal state the last gate run does
   * not describe, e.g. a rule an authoring loop gave up on. Forwarded to the
   * gate strip, which then reports the run as stopped rather than claiming a
   * verdict the entity contradicts.
   */
  gateRunAbandonedReason?: string | null;
  /**
   * Set when the tracked gate run is the publish that shipped what is live
   * rather than a check on a pending change. Forwarded to the gate strip,
   * which then reports the run as history instead of inviting a publish that
   * already happened.
   */
  gateRunDescribesLiveVersion?: boolean;
  /** A containing page may own the single checks strip for all of its tabs. */
  showGateProgress?: boolean;
}

/**
 * The shared code-workspace shell. Owns the four-zone layout (title bar / gate
 * rail / editor+panel row / optional bottom tabs) plus the three shared modals,
 * and renders **inline** — a domain that wants it in a full-page dialog wraps
 * it.
 *
 * Everything it renders comes from `config` (labels, gates, filenames, Monaco
 * models, tabs) and `vm` (state + handlers). There is no domain branch here.
 */
export const CodegenWorkspace = <
  GateKey extends string,
  TabKey extends string,
>({
  config,
  vm,
  capabilities,
  banners,
  headerDetails,
  recentRuns,
  showBottomSection = true,
  chrome = "full",
  readOnly,
  disablePersistedActions,
  disabledActionHint,
  onSelectGate,
  onAskAiFix,
  onClose,
  renderVersionCode,
  showVersionHistory = true,
  versionLimit = null,
  rollbackImpactCopy,
  gateRunAbandonedReason = null,
  gateRunDescribesLiveVersion = false,
  showGateProgress = true,
  isAiUpdating = false,
}: CodegenWorkspaceProps<GateKey, TabKey>) => {
  const [isAiCodePending, setIsAiCodePending] = React.useState(false);
  const { heightPx: splitHeight, ref: splitRef } =
    useMeasuredElementHeight<HTMLDivElement>();
  const [bodyView, setBodyView] = React.useState<WorkspaceBodyView>("primary");

  const nextVersionToPublish = (vm.activeVersion?.version ?? 0) + 1;

  // Two shell-level blockers, reported most-actionable-first:
  //   - a gate job in flight freezes every action, including Save — the run
  //     is executing the saved draft, so replacing it mid-run would make the
  //     gate results describe source that no longer exists.
  //   - pending edits block Run tests and Publish, which both act on the
  //     *saved* draft and would otherwise run or ship the previous source.
  // A caller-supplied hint wins: an unpersisted parent record is the harder
  // blocker, and its copy names the CTA that actually unblocks the workspace.
  const actionHint =
    disabledActionHint ??
    (vm.publish.isPublishing
      ? "Wait for the current run to finish."
      : vm.isDirty
        ? "Save your changes to run tests or publish."
        : undefined);

  // The checks strip is progress, not furniture. It appears while a save or
  // publish run is in flight and stays while the last run ended in a failure
  // the reader can act on from it (Run checks again / Ask Atlas to fix); an
  // idle or passed workspace shows nothing, since "All checks passed" beside
  // an editor the reader is typing into is a verdict on stale source.
  const gateRunPhase = summarizeGates(
    vm.publish.gates,
    config.gates,
    vm.publish.isPublishing,
    false,
    gateRunAbandonedReason !== null
  ).phase;
  const showGateStrip =
    vm.isSavingDraft ||
    vm.publish.isPublishing ||
    gateRunPhase === "running" ||
    gateRunPhase === "failed" ||
    gateRunPhase === "abandoned";

  return (
    <Stack sx={{ height: "100%", width: "100%", minHeight: 0 }}>
      {banners}

      {chrome === "full" ? (
        <TitleBar
          Icon={config.chrome.Icon}
          breadcrumbs={config.chrome.breadcrumbs}
          title={vm.entityName}
          untitledLabel={config.chrome.untitledLabel}
          statusLabel={vm.entityStatusLabel}
          activeVersion={vm.activeVersion?.version ?? null}
          hasUnsavedChanges={vm.isDirty}
          disabledActionHint={actionHint}
          isSaveDisabled={isWorkspaceSaveDisabled({
            isReadOnly: disablePersistedActions,
            isSavingDraft: vm.isSavingDraft,
            isPublishing: vm.publish.isPublishing,
            isSyntaxValid: vm.editor.syntax.isValid,
            isSyntaxPending: vm.editor.syntaxPending,
          })}
          runTests={
            capabilities.canRunGates
              ? {
                  isDisabled:
                    disablePersistedActions ||
                    vm.isDirty ||
                    vm.publish.isPublishing,
                }
              : null
          }
          isPublishDisabled={
            disablePersistedActions || vm.isDirty || vm.publish.isPublishing
          }
          unpublish={
            capabilities.canUnpublish && vm.activeVersion
              ? {
                  isDisabled:
                    disablePersistedActions ||
                    vm.publish.isPublishing ||
                    vm.isUnpublishing,
                }
              : null
          }
          onSaveDraft={vm.saveDraft}
          onRunTests={vm.runTests}
          onPublish={vm.openPublishModal}
          onUnpublish={vm.unpublishEntity}
          onClose={onClose}
        />
      ) : null}

      {headerDetails}

      {/* Code | Version history switch — the same control the template-rule
          Advanced tab renders, so both flavours switch views identically. */}
      <WorkspaceViewSwitch
        primaryLabel="Code"
        horizontalPadding={chrome === "embedded" ? 1 : 2}
        topPadding={chrome === "embedded" ? 2 : 4}
        bottomPadding={chrome === "embedded" ? 0 : 2}
        showRuns={recentRuns != null}
        showHistory={showVersionHistory}
        value={bodyView}
        onChange={setBodyView}
      />

      {/* Gate status follows the Advanced view selector, stays visible from
          Version history, and continues to describe the workspace below it. */}
      {showGateProgress && showGateStrip ? (
        <GateProgressStrip
          gates={vm.publish.gates}
          config={config.gates}
          heading={config.chrome.gatesHeading}
          isPublishing={vm.publish.isPublishing}
          horizontalMargin={1}
          topMargin={2}
          bottomMargin={chrome === "embedded" ? 1 : 0}
          abandonedReason={gateRunAbandonedReason}
          describesLiveVersion={gateRunDescribesLiveVersion}
          onRunChecksAgain={capabilities.canRunGates ? vm.runTests : undefined}
          runChecksAgainDisabled={
            disablePersistedActions || vm.isDirty || vm.publish.isPublishing
          }
          onAskAiFix={
            onAskAiFix
              ? () => {
                  setBodyView("primary");
                  onAskAiFix();
                }
              : undefined
          }
          onSelectGate={showBottomSection ? onSelectGate : undefined}
        />
      ) : null}

      {/* The Code view stays MOUNTED while Version history shows: the editor
          holds an unsaved buffer and a gate run may be in flight, and
          unmounting either to glance at history would silently discard both. */}
      <Box
        sx={{
          display: bodyView === "primary" ? "flex" : "none",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <Stack
          ref={splitRef}
          sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}
        >
          <Stack sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
            <EditorPanel
              editor={vm.editor}
              monaco={config.monaco}
              // Frozen while the AI holds the buffer: edits typed against
              // source that is about to be replaced are lost without ever
              // being saved, and the byline already says who is writing.
              readOnly={
                readOnly ||
                vm.publish.isPublishing ||
                isAiUpdating ||
                isAiCodePending
              }
              updatingAuthorName={
                isAiUpdating || isAiCodePending
                  ? config.chrome.aiAuthorName
                  : null
              }
              onChange={vm.setEditorValue}
              editorConsole={vm.editorConsole}
              controls={
                <ControlsPanel
                  config={config.controlsPanel}
                  aiChat={config.aiChat}
                  editorValue={vm.editor.value}
                  dependencies={vm.dependencies}
                  onAddDependency={vm.addDependency}
                  onRemoveDependency={vm.removeDependency}
                  onUpdateDependencyVersion={vm.updateDependencyVersion}
                  availableSecretNames={vm.availableSecretNames}
                  onManageSecrets={vm.openSecretsModal}
                  onApplySource={vm.applyAiSource}
                  onAiPendingChange={setIsAiCodePending}
                  activeVersion={vm.activeVersion}
                  versions={vm.versions}
                  onPublishVersion={vm.openRollbackModal}
                  readOnly={readOnly}
                />
              }
            />
          </Stack>

          {showBottomSection ? (
            <BottomSection
              selectedTab={vm.selectedTab}
              onSelectedTabChange={vm.setSelectedTab}
              tabs={config.tabs}
              maxHeight={
                // Before the first measurement there is no real ceiling to
                // clamp to; passing 0 would collapse the panel for a frame.
                splitHeight > 0
                  ? Math.max(0, splitHeight - EDITOR_ROW_MIN_HEIGHT)
                  : undefined
              }
            />
          ) : null}
        </Stack>
      </Box>

      {showVersionHistory && bodyView === "history" ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            p: 1,
          }}
        >
          <VersionHistoryPanel
            versions={vm.versions}
            draft={vm.draft}
            aiAuthorName={config.chrome.aiAuthorName}
            onPublishVersion={vm.openRollbackModal}
            onDeleteVersion={vm.deleteVersion}
            renderVersionCode={renderVersionCode}
            versionLimit={versionLimit}
            readOnly={readOnly}
          />
        </Box>
      ) : null}

      {bodyView === "runs" && recentRuns != null ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            p: 1,
          }}
        >
          {recentRuns}
        </Box>
      ) : null}

      {/* Modals */}
      <PublishReviewModal
        open={vm.isPublishModalOpen}
        onClose={vm.closePublishModal}
        onConfirm={vm.publishDraft}
        defaultTitle={vm.draft?.origin === "chat" ? "Built from chat" : ""}
        versionToPublish={nextVersionToPublish}
        previousVersionLabel={
          vm.activeVersion !== null ? `v${vm.activeVersion.version}` : "none"
        }
        config={config.publishReview}
        gates={vm.publish.gates}
        canPublish={vm.publish.canPublish}
        publishBlockReason={vm.publish.publishBlockReason}
        isPublishing={vm.publish.isPublishing}
      />
      <RollbackConfirmModal
        open={vm.rollbackTargetVersion !== null}
        onClose={vm.closeRollbackModal}
        onConfirm={vm.rollbackToVersion}
        targetVersion={vm.rollbackTargetVersion}
        versions={vm.versions}
        isRollingBack={vm.isRollingBack}
        impactCopy={rollbackImpactCopy}
      />
      <SecretsManagementModal
        open={vm.isSecretsModalOpen}
        onClose={vm.closeSecretsModal}
        secrets={vm.secrets}
        isLoading={vm.isLoading}
        scopeMessage={config.chrome.secretsScopeMessage}
        onCreateSecret={vm.createSecret}
        onDeleteSecret={vm.deleteSecret}
        isMutating={vm.isMutatingSecret}
      />
    </Stack>
  );
};
